import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePriceFeed } from '../context/PriceFeedContext';
import { Menu } from '@headlessui/react';
import CustomSelect from './ui/CustomSelect';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Modal } from './ui/modal';
import { listIndicatorPresets, createIndicatorPreset, updateIndicatorPreset, deleteIndicatorPreset } from '../api/api';
import DrawingToolbar from './DrawingToolbar';
import { Maximize2, Minimize2 } from 'lucide-react';

// Debug helpers
const DEBUG_IND = typeof window !== 'undefined' && (window.__DEBUG_INDICATORS ?? true);
const dlog = (...args) => { if (DEBUG_IND) console.log('[IndicatorControls]', ...args); };

// Stable event-callback helper to preserve function identity while using latest logic
function useEvent(fn) {
    const ref = useRef(fn);
    useEffect(() => { ref.current = fn; });
    return useCallback((...args) => ref.current?.(...args), []);
}

// Icon components moved to top-level so all components in this module can use them
const IconEye = (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
        <circle cx="12" cy="12" r="3"/>
    </svg>
);
const IconEyeOff = (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.77 21.77 0 0 1 5.06-6.94"/>
        <path d="M1 1l22 22"/>
    </svg>
);
const IconSliders = (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3"/>
        <circle cx="4" cy="14" r="2"/>
        <circle cx="12" cy="12" r="2"/>
        <circle cx="20" cy="16" r="2"/>
    </svg>
);
const IconTrash = (props) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
);

const CHART_TOOLBAR_BUTTON_STYLE = {
    textTransform: 'none',
    letterSpacing: '0.01em',
    fontWeight: 600,
};

const getChartToolbarLauncherClassName = (active, compact = false) => [
    'chart-toolbar-button',
    active ? 'chart-toolbar-launcher-active' : '',
    compact ? 'chart-toolbar-button-compact' : '',
].filter(Boolean).join(' ');

const getChartToolbarToggleClassName = (tone, active, compact = false) => [
    'chart-toolbar-button',
    active ? `chart-toolbar-toggle-on-${tone}` : 'chart-toolbar-toggle-off',
    compact ? 'chart-toolbar-button-compact' : '',
].filter(Boolean).join(' ');

const getChartToolbarIconClassName = (active, compact = false) => [
    'chart-toolbar-button',
    'chart-toolbar-icon-button',
    active ? 'chart-toolbar-launcher-active' : 'chart-toolbar-toggle-off',
    compact ? 'chart-toolbar-button-compact' : '',
].filter(Boolean).join(' ');

function FloatingProfilesMenuItems({ open, anchorRef, children }) {
    const [menuStyle, setMenuStyle] = useState({ top: -9999, left: -9999, width: 256 });

    const updatePosition = useCallback(() => {
        const anchor = anchorRef.current;
        if (!anchor || typeof window === 'undefined') return;

        const rect = anchor.getBoundingClientRect();
        const width = 256;
        const margin = 12;
        const left = Math.max(
            margin,
            Math.min(rect.right - width, window.innerWidth - width - margin)
        );

        setMenuStyle({
            top: rect.bottom + 8,
            left,
            width,
        });
    }, [anchorRef]);

    useEffect(() => {
        if (!open) return undefined;

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [open, updatePosition]);

    if (!open || typeof document === 'undefined') return null;

    return createPortal(
        <Menu.Items
            static
            className="fixed rounded-md bg-backgroundSurface border border-borderDefault shadow-lg focus:outline-none z-[120]"
            style={menuStyle}
        >
            {children}
        </Menu.Items>,
        document.body
    );
}

// Stable, top-level Profiles menu component
function ProfilesMenu({ compact, chartProfiles, selectedProfile, onLoadProfile, onDeleteProfile, setIsSaveProfileOpen }) {
    const profileList = Array.isArray(chartProfiles) ? chartProfiles : (Array.isArray(chartProfiles?.results) ? chartProfiles.results : []);
    const selectedProfileName = profileList.find((profile) => String(profile.id) === String(selectedProfile))?.name;
    const buttonRef = useRef(null);

    return (
        <Menu as="div" className="relative inline-block text-left">
            {({ open }) => (
                <>
                    <div>
                        <Menu.Button
                            ref={buttonRef}
                            as={Button}
                            size={compact ? 'sm' : 'md'}
                            variant="ghost"
                            className={getChartToolbarLauncherClassName(Boolean(selectedProfileName), compact)}
                            style={CHART_TOOLBAR_BUTTON_STYLE}
                        >
                            <span className="flex min-w-0 items-center gap-2">
                                <span>Profiles</span>
                                {!compact && selectedProfileName && (
                                    <span className="chart-toolbar-secondary-text max-w-[8rem] truncate" title={selectedProfileName}>
                                        {selectedProfileName}
                                    </span>
                                )}
                            </span>
                        </Menu.Button>
                    </div>
                    <FloatingProfilesMenuItems open={open} anchorRef={buttonRef}>
                        <div className="p-2 text-xs text-textSecondary">Load profile</div>
                        <div className="max-h-60 overflow-auto py-1">
                            {profileList.length > 0 ? (
                                profileList.map((p) => (
                                    <Menu.Item key={p.id}>
                                        {({ active }) => (
                                            <button
                                                className={`w-full text-left px-3 py-2 text-sm ${active ? 'bg-secondary/30' : ''}`}
                                                onClick={() => onLoadProfile && onLoadProfile(p.id)}
                                            >
                                                <span className="flex items-center gap-2">
                                                    <span className={`inline-block w-2 h-2 rounded-full ${selectedProfile === p.id ? 'bg-primary' : 'bg-transparent border border-borderDefault'}`} />
                                                    <span className={selectedProfile === p.id ? 'font-medium' : ''}>{p.name}</span>
                                                </span>
                                            </button>
                                        )}
                                    </Menu.Item>
                                ))
                            ) : (
                                <div className="px-3 py-2 text-sm text-textSecondary">No profiles</div>
                            )}
                        </div>
                        <div className="my-1 border-t border-borderDefault" />
                        <Menu.Item>
                            {({ active }) => (
                                <button
                                    className={`w-full text-left px-3 py-2 text-sm ${active ? 'bg-secondary/30' : ''}`}
                                    onClick={() => setIsSaveProfileOpen && setIsSaveProfileOpen(true)}
                                >
                                    Save current as...
                                </button>
                            )}
                        </Menu.Item>
                        <Menu.Item disabled={!selectedProfile}>
                            {({ active, disabled }) => (
                                <button
                                    className={`w-full text-left px-3 py-2 text-sm ${disabled ? 'opacity-50 cursor-not-allowed' : (active ? 'bg-secondary/30' : '')}`}
                                    onClick={() => {
                                        if (!selectedProfile) return;
                                        if (window.confirm('Delete the selected profile?')) {
                                            onDeleteProfile && onDeleteProfile(selectedProfile);
                                        }
                                    }}
                                >
                                    Delete selected
                                </button>
                            )}
                        </Menu.Item>
                    </FloatingProfilesMenuItems>
                </>
            )}
        </Menu>
    );
}

// Small component to test if the panel subtree remounts on ticks
/* eslint-disable no-unused-vars */
const PanelBody = React.memo(function PanelBody({
    activeIndicators,
    getPrimarySeriesKey,
    getEffectiveField,
    toggleIndicatorVisibility,
    openVisualEditor,
    onRemoveIndicator,
    indicatorOptions,
    selectedIndicator,
    setSelectedIndicator,
    getMetaById,
    params,
    handleParamChange,
    handleAddClick,
    // Presets
    indicatorPresets,
    selectedPresetId,
    setSelectedPresetId,
    onApplySelectedPreset,
    onSaveCurrentAsPreset,
    onUpdateSelectedPresetParams,
    onRenameSelectedPreset,
    onDeleteSelectedPreset,
}) {
    useEffect(() => {
        dlog('PanelBody mounted');
        return () => dlog('PanelBody unmounted');
    }, []);

    return (
        <>
            {/* Active indicators list removed; chips on the chart/panes replace this UI */}

            <div className="p-3 pt-1" onMouseDownCapture={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                {indicatorOptions.length === 0 ? (
                    <div className="text-sm text-textSecondary">No indicators available.</div>
                ) : (
                    <CustomSelect
                        options={indicatorOptions}
                        value={selectedIndicator}
                        onChange={setSelectedIndicator}
                        placeholder="Select Indicator"
                        searchable={true}
                        searchPlaceholder="Search indicators…"
                        // Use portal rendering so the dropdown floats outside the modal's
                        // overflow container, avoiding a second scrollbar.
                        renderInPortal={true}
                        // Disable virtualization — indicator list is small (~33 items)
                        // and virtual scroll desyncs inside modals
                        virtualize={false}
                    />
                )}

                {selectedIndicator && getMetaById(selectedIndicator)?.params_schema && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                        {Object.entries(getMetaById(selectedIndicator).params_schema)
                            .sort(([, a], [, b]) => {
                                const ao = (a && (a.x_order ?? a['x-order'])) ?? Number.POSITIVE_INFINITY;
                                const bo = (b && (b.x_order ?? b['x-order'])) ?? Number.POSITIVE_INFINITY;
                                return ao - bo;
                            })
                            .map(([paramName, paramSchema]) => (
                            <div key={paramName}>
                                <label className="block text-xs font-medium mb-1">{paramSchema.display_name}</label>
                                {/* Numeric types: support integer, float, number (and alias int) */}
                                {(paramSchema.type === 'integer' || paramSchema.type === 'int' || paramSchema.type === 'float' || paramSchema.type === 'number') && (
                                    <Input
                                        type="number"
                                        className="w-1/2 min-w-[140px]"
                                        value={params[paramName] ?? ''}
                                        onChange={e => {
                                            const raw = e.target.value;
                                            // Allow clearing the input
                                            if (raw === '') { handleParamChange(paramName, ''); return; }
                                            const isInt = (paramSchema.type === 'integer' || paramSchema.type === 'int');
                                            const v = isInt ? parseInt(raw, 10) : parseFloat(raw);
                                            handleParamChange(paramName, Number.isNaN(v) ? '' : v);
                                        }}
                                        min={(paramSchema.min ?? paramSchema.minimum)}
                                        max={(paramSchema.max ?? paramSchema.maximum)}
                                        step={paramSchema.step || ((paramSchema.type === 'integer' || paramSchema.type === 'int') ? 1 : 'any')}
                                    />
                                )}
                                {/* String with options or enum */}
                                {paramSchema.type === 'string' && (Array.isArray(paramSchema.options) || Array.isArray(paramSchema.enum)) && (
                                    <CustomSelect
                                        options={(paramSchema.options || paramSchema.enum).map(opt => ({ value: opt, label: String(opt) }))}
                                        value={params[paramName] || ''}
                                        onChange={value => handleParamChange(paramName, value)}
                                        className="w-1/2 min-w-[140px]"
                                        // Render inline to keep interactions inside the Popover panel
                                        renderInPortal={false}
                                    />
                                )}
                                {/* Plain string without options */}
                                {paramSchema.type === 'string' && !Array.isArray(paramSchema.options) && !Array.isArray(paramSchema.enum) && (
                                    <Input
                                        type="text"
                                        className="w-1/2 min-w-[140px]"
                                        value={params[paramName] ?? ''}
                                        onChange={e => handleParamChange(paramName, e.target.value)}
                                    />
                                )}
                                {/* Boolean (defensive) */}
                                {paramSchema.type === 'boolean' && (
                                    <div className="flex items-center gap-2">
                                        <input
                                            id={`param-${paramName}`}
                                            type="checkbox"
                                            checked={Boolean(params[paramName])}
                                            onChange={e => handleParamChange(paramName, e.target.checked)}
                                        />
                                        <label htmlFor={`param-${paramName}`} className="text-xs">{paramSchema.display_name}</label>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Presets section */}
                {selectedIndicator && (
                    <div className="mt-4">
                        <div className="mb-1 text-xs text-textSecondary">Presets</div>
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="min-w-[220px] w-1/2 max-w-sm">
                                <CustomSelect
                                    options={(indicatorPresets || []).map(p => ({ value: String(p.id), label: p.name }))}
                                    value={selectedPresetId}
                                    onChange={setSelectedPresetId}
                                    placeholder={(indicatorPresets && indicatorPresets.length) ? 'Choose preset' : 'No presets'}
                                    renderInPortal={false}
                                />
                            </div>
                            <Button size="sm" variant="secondary" onClick={onApplySelectedPreset} disabled={!selectedPresetId}>Apply</Button>
                            <Button size="sm" variant="secondary" onClick={onSaveCurrentAsPreset} disabled={!selectedIndicator}>Save as</Button>
                            <Button size="sm" variant="outline" onClick={onUpdateSelectedPresetParams} disabled={!selectedPresetId}>Update</Button>
                            <Button size="sm" variant="outline" onClick={onRenameSelectedPreset} disabled={!selectedPresetId}>Rename</Button>
                            <Button size="sm" variant="destructive" onClick={onDeleteSelectedPreset} disabled={!selectedPresetId}>Delete</Button>
                        </div>
                    </div>
                )}

                <div className="mt-3 flex justify-end">
                    <Button onClick={handleAddClick} disabled={!selectedIndicator}>Add</Button>
                </div>
            </div>
        </>
    );
});

// Stable, top-level Indicators menu component
const IndicatorsMenu = React.memo(function IndicatorsMenu({
    compact,
    activeIndicators,
    getPrimarySeriesKey,
    getEffectiveField,
    toggleIndicatorVisibility,
    openVisualEditor,
    onRemoveIndicator,
    indicatorOptions,
    selectedIndicator,
    setSelectedIndicator,
    getMetaById,
    getMetaByName,
    params,
    handleParamChange,
    handleAddClick,
    // New: indicator-on-indicator state from parent
    selectedSource,
    setSelectedSource,
    selectedSourceOutput,
    setSelectedSourceOutput,
    // Presets
    indicatorPresets,
    selectedPresetId,
    setSelectedPresetId,
    onApplySelectedPreset,
    onSaveCurrentAsPreset,
    onUpdateSelectedPresetParams,
    onRenameSelectedPreset,
    onDeleteSelectedPreset,
}) {
    const [isOpen, setIsOpen] = useState(false);
    // Render/mount diagnostics
    const renderCountRef = useRef(0);
    const prevOptionsRef = useRef(indicatorOptions);
    const prevActiveLenRef = useRef(activeIndicators.length);

    useEffect(() => {
        renderCountRef.current += 1;
        dlog('IndicatorsMenu render', {
            renderCount: renderCountRef.current,
            activeLen: activeIndicators.length,
            optionsLen: indicatorOptions.length,
            selectedIndicator,
        });
    });

    useEffect(() => {
        dlog('IndicatorsMenu mounted');
        return () => dlog('IndicatorsMenu unmounted');
    }, []);

    useEffect(() => {
        if (prevOptionsRef.current !== indicatorOptions) {
            const sameLength = (prevOptionsRef.current?.length ?? -1) === indicatorOptions.length;
            dlog('indicatorOptions ref changed', { sameLength, newLen: indicatorOptions.length });
            prevOptionsRef.current = indicatorOptions;
        }
    }, [indicatorOptions]);

    useEffect(() => {
        if (prevActiveLenRef.current !== activeIndicators.length) {
            dlog('activeIndicators length changed', { from: prevActiveLenRef.current, to: activeIndicators.length });
            prevActiveLenRef.current = activeIndicators.length;
        }
    }, [activeIndicators.length]);

    const activeIndicatorCount = Array.isArray(activeIndicators) ? activeIndicators.length : 0;

    return (
        <>
            <Button
                size={compact ? 'sm' : 'md'}
                variant="ghost"
                className={getChartToolbarLauncherClassName(isOpen || activeIndicatorCount > 0, compact)}
                style={CHART_TOOLBAR_BUTTON_STYLE}
                onClick={() => setIsOpen(true)}
            >
                <span className="flex items-center gap-2">
                    <span>Indicators</span>
                    {activeIndicatorCount > 0 && <span className="chart-toolbar-badge">{activeIndicatorCount}</span>}
                </span>
            </Button>
            <Modal
                open={isOpen}
                onClose={() => setIsOpen(false)}
                title="Indicators"
                maxWidthClass="max-w-md"
                panelClassName="w-full max-w-md h-auto max-h-[85vh] flex flex-col"
                contentClassName="flex-1 overflow-y-auto overflow-x-hidden"
            >
                <PanelBody
                    activeIndicators={activeIndicators}
                    getPrimarySeriesKey={getPrimarySeriesKey}
                    getEffectiveField={getEffectiveField}
                    toggleIndicatorVisibility={toggleIndicatorVisibility}
                    openVisualEditor={openVisualEditor}
                    onRemoveIndicator={onRemoveIndicator}
                    indicatorOptions={indicatorOptions}
                    selectedIndicator={selectedIndicator}
                    setSelectedIndicator={setSelectedIndicator}
                    getMetaById={getMetaById}
                    params={params}
                    handleParamChange={handleParamChange}
                    handleAddClick={handleAddClick}
                    indicatorPresets={indicatorPresets}
                    selectedPresetId={selectedPresetId}
                    setSelectedPresetId={setSelectedPresetId}
                    onApplySelectedPreset={onApplySelectedPreset}
                    onSaveCurrentAsPreset={onSaveCurrentAsPreset}
                    onUpdateSelectedPresetParams={onUpdateSelectedPresetParams}
                    onRenameSelectedPreset={onRenameSelectedPreset}
                    onDeleteSelectedPreset={onDeleteSelectedPreset}
                />
                {/* Indicator-on-indicator: choose source indicator/output (optional) */}
                <div className="px-3 pb-3">
                    <div className="mt-2 text-xs text-textSecondary">Source</div>
                    <select
                        className="mt-1 w-full text-sm bg-backgroundInput text-textPrimary border border-borderDefault rounded px-2 py-1"
                        value={selectedSource}
                        onChange={(e) => { setSelectedSource(e.target.value); setSelectedSourceOutput(''); }}
                    >
                        <option value="PRICE:close">Price (close)</option>
                        {Array.isArray(activeIndicators) && activeIndicators.map(ind => (
                            <option key={ind.id} value={`IND:${ind.id}`}>{ind.name}</option>
                        ))}
                    </select>
                    {selectedSource.startsWith('IND:') && (() => {
                        const srcId = selectedSource.slice(4);
                        const src = activeIndicators.find(i => i.id === srcId);
                        const meta = src ? getMetaByName(src.name) : null;
                        const outs = Array.isArray(meta?.outputs) ? meta.outputs : [];
                        if (outs.length <= 1) return null;
                        return (
                            <div className="mt-2">
                                <div className="text-xs text-textSecondary">Use output</div>
                                <select
                                    className="mt-1 w-full text-sm bg-backgroundInput text-textPrimary border border-borderDefault rounded px-2 py-1"
                                    value={selectedSourceOutput}
                                    onChange={(e) => setSelectedSourceOutput(e.target.value)}
                                >
                                    <option value="">default</option>
                                    {outs.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </div>
                        );
                    })()}
                </div>
            </Modal>
        </>
    );
}, (prev, next) => {
    // Custom comparator to log which props changed
    const diffs = [];
    const compare = (k) => { if (prev[k] !== next[k]) diffs.push(k); };
    ['compact','activeIndicators','getPrimarySeriesKey','getEffectiveField','toggleIndicatorVisibility','openVisualEditor','onRemoveIndicator','indicatorOptions','selectedIndicator','setSelectedIndicator','getMetaById','getMetaByName','params','handleParamChange','handleAddClick','indicatorPresets','selectedPresetId','setSelectedPresetId','onApplySelectedPreset','onSaveCurrentAsPreset','onUpdateSelectedPresetParams','onRenameSelectedPreset','onDeleteSelectedPreset'].forEach(compare);

    if (diffs.length) {
        dlog('IndicatorsMenu props changed', diffs, {
            prevActiveLen: prev.activeIndicators?.length,
            nextActiveLen: next.activeIndicators?.length,
            prevOptsLen: prev.indicatorOptions?.length,
            nextOptsLen: next.indicatorOptions?.length,
        });
    }
    // Only re-render if something actually changed by reference
    return diffs.length === 0;
});

const IndicatorControls = ({ 
    onAddIndicator, 
    onRemoveIndicator, 
    onUpdateIndicatorParams,
    activeIndicators,
    availableIndicators,
    chartProfiles,
    selectedProfile,
    onLoadProfile,
    onSaveProfile,
    onDeleteProfile,
    onUpdateIndicatorVisuals,
    isBacktest = false,
    compact = false,
    overlay = false,
    toolbarTitle,
    // New: recent trades overlay toggle
    showRecentTrades,
    onToggleRecentTrades,
    // New: volume overlay toggle
    showVolume,
    onToggleVolume,
    // New: timeframe control props (to show in same row as buttons)
    timeframeOptions,
    selectedTimeframe,
    onTimeframeChange,
    isFullscreen = false,
    onToggleFullscreen,
    // Drawing tool props
    activeDrawingTool,
    onSelectDrawingTool,
    onDeleteSelectedDrawing,
    onDeleteAllDrawings,
    onUpdateDrawing,
    onSelectDrawing,
    drawingSelection,
    selectedDrawingId,
    drawingCount,
    drawings,
}) => {
    // Normalize availableIndicators into two access patterns:
    // 1) id->meta (backend key) for adding indicators
    // 2) name->meta for visuals against active indicators' .name
    const toIdMap = (src) => {
        const map = new Map();
        if (!src) return map;
        if (Array.isArray(src)) {
            src.forEach((m) => {
                if (!m) return;
                const id = m.id || m.name || m.display_name;
                if (id) map.set(id, m);
            });
            return map;
        }
        if (typeof src === 'object') {
            if (src.indicators && typeof src.indicators === 'object') {
                Object.entries(src.indicators).forEach(([key, m]) => { if (m) map.set(key, m); });
            }
            Object.entries(src).forEach(([key, m]) => {
                if (key === 'indicators') return;
                if (!m || typeof m !== 'object') return;
                map.set(key, m);
            });
        }
        return map;
    };

    const toNameMap = (src) => {
        const map = new Map();
        if (!src) return map;
        if (Array.isArray(src)) {
            src.forEach((m) => { if (m) map.set(m.name || m.display_name, m); });
        } else if (typeof src === 'object') {
            const put = (m, key) => { if (!m) return; const nm = m.name || m.display_name || key; if (nm) map.set(nm, m); };
            if (src.indicators && typeof src.indicators === 'object') {
                Object.entries(src.indicators).forEach(([k, m]) => put(m, k));
            }
            Object.entries(src).forEach(([k, m]) => { if (k !== 'indicators' && typeof m === 'object') put(m, k); });
        }
        return map;
    };

    // Memoize maps so they don't change identity on each render (prevents dropdown reset on tick)
    const idMap = useMemo(() => toIdMap(availableIndicators), [availableIndicators]);
    const nameMap = useMemo(() => toNameMap(availableIndicators), [availableIndicators]);

    // Build indicator options from idMap (backend keys)
    const indicatorOptions = useMemo(() => {
        const opts = Array.from(idMap.entries()).map(([id, meta]) => ({
            value: id,
            label: meta?.display_name || meta?.name || id,
        }));
        dlog('indicatorOptions recomputed', { len: opts.length });
        return opts;
    }, [idMap]);

    // Diagnostics for upstream changes
    const prevIdMapRef = useRef(idMap);
    const prevNameMapRef = useRef(nameMap);
    useEffect(() => {
        if (prevIdMapRef.current !== idMap) dlog('idMap ref changed');
        if (prevNameMapRef.current !== nameMap) dlog('nameMap ref changed');
        prevIdMapRef.current = idMap;
        prevNameMapRef.current = nameMap;
    }, [idMap, nameMap]);

    const getMetaById = useCallback((id) => {
        if (!id) return null;
        return idMap.get(id) || null;
    }, [idMap]);

    const getMetaByName = useCallback((name) => {
        if (!name) return null;
        return nameMap.get(name) || null;
    }, [nameMap]);

    // Local UI state for selection/params (reused inside the menu)
    const [selectedIndicator, setSelectedIndicator] = useState('');
    const [params, setParams] = useState({});
    // New: indicator-on-indicator source selection
    const [selectedSource, setSelectedSource] = useState('PRICE:close'); // 'PRICE:close' or `IND:<activeId>`
    const [selectedSourceOutput, setSelectedSourceOutput] = useState('');
    const [newProfileName, setNewProfileName] = useState('');
    // Removed Add Indicator modal state in favor of the unified menu
    // const [isAddOpen, setIsAddOpen] = useState(false);
    const [isSaveProfileOpen, setIsSaveProfileOpen] = useState(false);
    // Presets state for Add flow
    const [indicatorPresets, setIndicatorPresets] = useState([]);
    const [selectedPresetId, setSelectedPresetId] = useState('');

    // Collapsed view for compact mode
    const [collapsed, setCollapsed] = useState(compact);
    useEffect(() => { setCollapsed(compact); }, [compact]);

    // Local UI state for visuals editor
    const [editingIndicatorId, setEditingIndicatorId] = useState(null);
    const [visualDraft, setVisualDraft] = useState({});
    const [paramDraft, setParamDraft] = useState({});
    const [editorTab, setEditorTab] = useState('visuals'); // 'visuals' | 'params'
    const [justSaved, setJustSaved] = useState(false);
    const saveDebounceRef = useRef(null);
    // Edit-flow presets
    const [editIndicatorPresets, setEditIndicatorPresets] = useState([]);
    const [editSelectedPresetId, setEditSelectedPresetId] = useState('');
    // Stable key for the editing indicator's registry id (set when opening editor)
    const editRegistryKeyRef = useRef('');

    useEffect(() => {
        const meta = getMetaById(selectedIndicator);
        if (selectedIndicator && meta?.params_schema) {
            const defaultParams = {};
            Object.entries(meta.params_schema).forEach(([paramName, paramSchema]) => {
                defaultParams[paramName] = paramSchema.default;
            });
            setParams(defaultParams);
        } else {
            setParams({});
        }
    }, [selectedIndicator, availableIndicators, getMetaById]);

    // Load presets when selectedIndicator changes
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setSelectedPresetId('');
            setIndicatorPresets([]);
            const registryKey = selectedIndicator;
            if (!registryKey) return;
            try {
                const data = await listIndicatorPresets({ indicator: registryKey });
                if (!cancelled) setIndicatorPresets(Array.isArray(data) ? data : (data?.results || []));
            } catch (e) {
                console.warn('Failed to load indicator presets', e);
                if (!cancelled) setIndicatorPresets([]);
            }
        };
        if (selectedIndicator) load();
        return () => { cancelled = true; };
    }, [selectedIndicator]);

    // Presets handlers
    const onApplySelectedPreset = useCallback(() => {
        if (!selectedPresetId) return;
        const preset = indicatorPresets.find(p => String(p.id) === String(selectedPresetId));
        if (!preset || !preset.params) return;
        setParams(prev => ({ ...prev, ...preset.params }));
    }, [selectedPresetId, indicatorPresets]);

    const onSaveCurrentAsPreset = useCallback(async () => {
        const meta = getMetaById(selectedIndicator);
        const registryKey = selectedIndicator || (meta?.name || meta?.display_name);
        if (!registryKey) return;
        const name = window.prompt('Preset name?');
        if (!name) return;
        try {
            const payload = { indicator: registryKey, name, params };
            const created = await createIndicatorPreset(payload);
            setIndicatorPresets(prev => [created, ...prev]);
            setSelectedPresetId(String(created.id));
        } catch (e) {
            alert(e?.message || 'Failed to create preset');
        }
    }, [selectedIndicator, params, getMetaById]);

    const onUpdateSelectedPresetParams = useCallback(async () => {
        if (!selectedPresetId) return;
        try {
            await updateIndicatorPreset(selectedPresetId, { params });
            // update local copy
            setIndicatorPresets(prev => prev.map(p => String(p.id) === String(selectedPresetId) ? { ...p, params: { ...params } } : p));
        } catch (e) {
            alert(e?.message || 'Failed to update preset');
        }
    }, [selectedPresetId, params]);

    const onRenameSelectedPreset = useCallback(async () => {
        if (!selectedPresetId) return;
        const name = window.prompt('New preset name?');
        if (!name) return;
        try {
            await updateIndicatorPreset(selectedPresetId, { name });
            setIndicatorPresets(prev => prev.map(p => String(p.id) === String(selectedPresetId) ? { ...p, name } : p));
        } catch (e) {
            alert(e?.message || 'Failed to rename preset');
        }
    }, [selectedPresetId]);

    const onDeleteSelectedPreset = useCallback(async () => {
        if (!selectedPresetId) return;
        if (!window.confirm('Delete this preset?')) return;
        try {
            await deleteIndicatorPreset(selectedPresetId);
            setIndicatorPresets(prev => prev.filter(p => String(p.id) !== String(selectedPresetId)));
            setSelectedPresetId('');
        } catch (e) {
            alert(e?.message || 'Failed to delete preset');
        }
    }, [selectedPresetId]);

    const handleParamChange = useCallback((paramName, value) => {
        setParams(prevParams => ({ ...prevParams, [paramName]: value }));
    }, []);

    // Access subscribeSpec from context (guard for pages without PriceFeedProvider, e.g., backtest)
    const priceFeedCtx = usePriceFeed();
    const subscribeSpec = priceFeedCtx?.subscribeSpec;

    // Find backend id key by indicator display/name (case-insensitive)
    const getIdByName = useCallback((name) => {
        if (!name) return null;
        const lower = String(name).toLowerCase();
        for (const [id, meta] of idMap.entries()) {
            const n = (meta?.name || meta?.display_name || id);
            if (String(n).toLowerCase() === lower) return id;
        }
        return null;
    }, [idMap]);

    const handleAddClick = useCallback(() => {
        if (!selectedIndicator) return;
        const indicatorInfo = getMetaById(selectedIndicator);
        if (!indicatorInfo) return;
        // If source is PRICE, use legacy add flow
        if (!selectedSource || selectedSource.startsWith('PRICE')) {
            onAddIndicator(selectedIndicator, params, indicatorInfo.pane_type, indicatorInfo.scale_type);
        } else if (selectedSource.startsWith('IND:')) {
            // Compose nested spec: child(selectedIndicator) on parent(source indicator)
            const srcId = selectedSource.slice(4);
            const src = activeIndicators.find(i => i.id === srcId);
            if (!src) return;
            // Resolve backend ids for child and parent names
            const childId = selectedIndicator; // already an id from list
            const parentId = getIdByName(src.name) || String(src.name).toLowerCase();
            // Build parent node: pass PRICE context so indicators needing OHLC/volume can derive as needed
            const parentNode = {
                name: parentId,
                params: src.params || {},
                inputs: [ { ref: 'PRICE', params: { field: 'close' } } ],
            };
            if (selectedSourceOutput) parentNode.output = selectedSourceOutput;
            const nestedSpec = {
                name: childId,
                params: params || {},
                inputs: [ parentNode ],
            };
            if (isBacktest) {
                // In backtest mode, send the nested spec to the parent via onAddIndicator
                onAddIndicator(nestedSpec);
            } else {
                // Live mode: subscribe via WS to render as overlay
                const uid = `spec:${childId}:on:${parentId}:${selectedSourceOutput || 'value'}:${JSON.stringify(params)}:${JSON.stringify(src.params)}`;
                try {
                    const attachToMeta = { parentId: srcId, parentName: src.name, parentParams: src.params || {} };
                    subscribeSpec && subscribeSpec(uid, nestedSpec, { timeframe: selectedTimeframe, count: 2500, attachToId: srcId, attachToMeta });
                } catch (e) {
                    console.error('Failed to subscribe nested spec:', e);
                }
            }
        }
        // reset form after adding
        setSelectedIndicator('');
        setParams({});
        setSelectedSource('PRICE:close');
        setSelectedSourceOutput('');
    }, [selectedIndicator, params, selectedSource, selectedSourceOutput, getMetaById, onAddIndicator, activeIndicators, subscribeSpec, selectedTimeframe, getIdByName, isBacktest]);

    // --- Profiles ---
    const handleSaveProfileClick = () => {
        if (newProfileName.trim()) {
            onSaveProfile(newProfileName.trim());
            setNewProfileName('');
            setIsSaveProfileOpen(false);
        } else {
            alert('Please enter a name for the profile.');
        }
    };

    // Helpers: deep merge for effective visuals
    const deepMerge = useCallback((a, b) => {
        if (!b) return a || {};
        if (!a) return b || {};
        const out = { ...a };
        Object.keys(b).forEach((k) => {
            const bv = b[k];
            const av = out[k];
            if (bv && typeof bv === 'object' && !Array.isArray(bv)) {
                out[k] = deepMerge(av && typeof av === 'object' ? av : {}, bv);
            } else {
                out[k] = bv;
            }
        });
        return out;
    }, []);

    // Robust meta lookup for activeIndicators (by indicator.name)
    const getIndicatorMeta = useCallback((indicator) => {
        if (!indicator) return null;
        const name = indicator.name || indicator.display_name;
        if (!name) return null;
        return getMetaByName(name);
    }, [getMetaByName]);

    const getEffectiveVisuals = useCallback((indicator) => {
        const meta = getIndicatorMeta(indicator);
        const defaults = meta?.visual_defaults || {};
        const overrides = indicator?.visual_overrides || {};
        return deepMerge(defaults, overrides);
    }, [getIndicatorMeta, deepMerge]);

    const getSeriesKeys = useCallback((indicator) => {
        const meta = getIndicatorMeta(indicator);
        const schema = meta?.visual_schema || {};
        const defaults = meta?.visual_defaults || {};
        const keys = Object.keys((schema.series || defaults.series || {}));
        if (keys.length === 0) return ['default'];
        return keys;
    }, [getIndicatorMeta]);

    const getPrimarySeriesKey = useCallback((indicator) => {
        const keys = getSeriesKeys(indicator);
        return keys.includes('default') ? 'default' : keys[0];
    }, [getSeriesKeys]);

    const getEffectiveField = useCallback((indicator, seriesKey, field, fallback) => {
        const eff = getEffectiveVisuals(indicator);
        const v = eff?.series?.[seriesKey]?.[field];
        return v !== undefined ? v : fallback;
    }, [getEffectiveVisuals]);

    const openVisualEditor = useCallback((indicator) => {
        const eff = getEffectiveVisuals(indicator);
        setVisualDraft(eff);
        // Seed paramDraft from the indicator's current params
        try { setParamDraft({ ...(indicator?.params || {}) }); } catch { setParamDraft({}); }
        setEditorTab('visuals');
        setEditingIndicatorId(indicator.id);
        // Capture a stable registry key for preset queries
        try {
            const regKey = getIdByName(indicator.name) || indicator.name || '';
            editRegistryKeyRef.current = regKey ? String(regKey) : '';
        } catch { editRegistryKeyRef.current = ''; }
    }, [getEffectiveVisuals]);

    const closeVisualEditor = () => {
        setEditingIndicatorId(null);
        setVisualDraft({});
        setParamDraft({});
        setEditorTab('visuals');
        setEditIndicatorPresets([]);
        setEditSelectedPresetId('');
    };

    const handleVisualFieldChange = (seriesKey, field, schema, rawValue) => {
        let value = rawValue;
        if (schema?.type === 'integer') {
            value = parseInt(rawValue, 10);
            if (Number.isNaN(value)) value = undefined;
            if (typeof schema.min === 'number') value = Math.max(schema.min, value);
            if (typeof schema.max === 'number') value = Math.min(schema.max, value);
        }
        if (schema?.type === 'boolean') {
            value = Boolean(rawValue);
        }
        if (schema?.enum && Array.isArray(schema.enum) && !schema.enum.includes(value)) {
            // ignore invalid choice
            return;
        }
        setVisualDraft((prev) => ({
            ...prev,
            series: {
                ...(prev.series || {}),
                [seriesKey]: {
                    ...((prev.series || {})[seriesKey] || {}),
                    [field]: value,
                },
            },
        }));
    };

    // Build minimal overrides against defaults
    const buildOverrides = useCallback((indicator, draft) => {
        const meta = getIndicatorMeta(indicator);
        const defaults = meta?.visual_defaults || {};
        const overrides = { series: {} };
        const draftSeries = draft.series || {};
        const defaultSeries = defaults.series || {};
        Object.keys(draftSeries).forEach((k) => {
            const d = draftSeries[k] || {};
            const def = defaultSeries[k] || {};
            const diff = {};
            Object.keys(d).forEach((f) => {
                if (d[f] !== def[f] && d[f] !== undefined) diff[f] = d[f];
            });
            if (Object.keys(diff).length) overrides.series[k] = diff;
        });
        return overrides;
    }, [getIndicatorMeta]);

    const saveVisuals = (silent = false) => {
        if (!editingIndicatorId) return;
        const indicator = activeIndicators.find((i) => i.id === editingIndicatorId);
        if (!indicator) return;
        const overrides = buildOverrides(indicator, visualDraft);
        onUpdateIndicatorVisuals && onUpdateIndicatorVisuals(editingIndicatorId, overrides);
        if (!silent) closeVisualEditor();
        setJustSaved(true);
        window.setTimeout(() => setJustSaved(false), 800);
    };

    const saveParams = useCallback(() => {
        if (!editingIndicatorId || !onUpdateIndicatorParams) return;
        onUpdateIndicatorParams(editingIndicatorId, paramDraft);
        closeVisualEditor();
    }, [editingIndicatorId, onUpdateIndicatorParams, paramDraft]);

    // Auto-apply with debounce when editing
    useEffect(() => {
        if (!editingIndicatorId) return;
        if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
        saveDebounceRef.current = setTimeout(() => {
            saveVisuals(true);
        }, 250);
        return () => {
            if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visualDraft, editingIndicatorId]);

    // Visibility toggle per-indicator (applies to all series keys of that indicator)
    const toggleIndicatorVisibility = useCallback((indicator) => {
        const eff = getEffectiveVisuals(indicator);
        const keys = Object.keys(eff.series || getSeriesKeys(indicator).reduce((o, k) => ({...o, [k]: {}}), {}));
        // Determine current overall visibility (true by default unless any key explicitly false)
        const anyVisible = keys.some(k => eff.series?.[k]?.visible !== false);
        const nextVisible = !anyVisible;
        const nextDraft = { series: {} };
        keys.forEach((k) => {
            nextDraft.series[k] = { ...(eff.series?.[k] || {}), visible: nextVisible };
        });
        const overrides = buildOverrides(indicator, nextDraft);
        onUpdateIndicatorVisuals && onUpdateIndicatorVisuals(indicator.id, overrides);
    }, [getEffectiveVisuals, getSeriesKeys, buildOverrides, onUpdateIndicatorVisuals]);

    // Stable wrappers for props that may change identity upstream or due to deps
    const stableOnRemoveIndicator = useEvent((id) => onRemoveIndicator(id));
    const stableHandleAddClick = useEvent(() => handleAddClick());
    const stableToggleIndicatorVisibility = useEvent((indicator) => toggleIndicatorVisibility(indicator));
    const stableOpenVisualEditor = useEvent((indicator) => openVisualEditor(indicator));

    // Compute editing context for modal
    const editingIndicator = activeIndicators.find((i) => i.id === editingIndicatorId);
    const editingMeta = editingIndicator ? (getIndicatorMeta(editingIndicator) || {}) : {};
    const editingSchema = editingMeta?.visual_schema || {};
    const editingDefaults = editingMeta?.visual_defaults || {};
    const editingParamSchema = editingMeta?.params_schema || null;

    // Bridge: listen for external requests to open visual editor (from chart overlay chips)
    useEffect(() => {
        const onOpenEditor = (e) => {
            const id = e?.detail?.indicatorId;
            if (!id) return;
            const ind = activeIndicators.find((i) => i.id === id);
            if (ind) openVisualEditor(ind);
        };
        window.addEventListener('open-indicator-visual-editor', onOpenEditor);
        return () => window.removeEventListener('open-indicator-visual-editor', onOpenEditor);
    }, [activeIndicators, openVisualEditor]);

    // Load presets for the editing indicator when modal opens or target changes
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setEditSelectedPresetId('');
            setEditIndicatorPresets([]);
            const regKey = editRegistryKeyRef.current;
            if (!regKey) return;
            try {
                const data = await listIndicatorPresets({ indicator: regKey });
                if (!cancelled) setEditIndicatorPresets(Array.isArray(data) ? data : (data?.results || []));
            } catch (e) {
                console.warn('Failed to load edit presets', e);
                if (!cancelled) setEditIndicatorPresets([]);
            }
        };
        if (editingIndicatorId) load();
        return () => { cancelled = true; };
    }, [editingIndicatorId]);

    const applyEditPreset = useCallback(() => {
        if (!editSelectedPresetId) return;
        const preset = editIndicatorPresets.find(p => String(p.id) === String(editSelectedPresetId));
        if (!preset || !preset.params) return;
        setParamDraft(prev => ({ ...prev, ...preset.params }));
    }, [editSelectedPresetId, editIndicatorPresets]);

    const saveAsEditPreset = useCallback(async () => {
        if (!editingIndicatorId) return;
        const ind = activeIndicators.find((i) => i.id === editingIndicatorId);
        if (!ind) return;
        const regKey = getIdByName(ind.name) || ind.name;
        const name = window.prompt('Preset name?');
        if (!name) return;
        try {
            const created = await createIndicatorPreset({ indicator: regKey, name, params: paramDraft });
            setEditIndicatorPresets(prev => [created, ...prev]);
            setEditSelectedPresetId(String(created.id));
        } catch (e) {
            alert(e?.message || 'Failed to create preset');
        }
    }, [editingIndicatorId, activeIndicators, getIdByName, paramDraft]);

    const updateEditPresetParams = useCallback(async () => {
        if (!editSelectedPresetId) return;
        try {
            await updateIndicatorPreset(editSelectedPresetId, { params: paramDraft });
            setEditIndicatorPresets(prev => prev.map(p => String(p.id) === String(editSelectedPresetId) ? { ...p, params: { ...paramDraft } } : p));
        } catch (e) {
            alert(e?.message || 'Failed to update preset');
        }
    }, [editSelectedPresetId, paramDraft]);

    const renameEditPreset = useCallback(async () => {
        if (!editSelectedPresetId) return;
        const name = window.prompt('New preset name?');
        if (!name) return;
        try {
            await updateIndicatorPreset(editSelectedPresetId, { name });
            setEditIndicatorPresets(prev => prev.map(p => String(p.id) === String(editSelectedPresetId) ? { ...p, name } : p));
        } catch (e) {
            alert(e?.message || 'Failed to rename preset');
        }
    }, [editSelectedPresetId]);

    const deleteEditPreset = useCallback(async () => {
        if (!editSelectedPresetId) return;
        if (!window.confirm('Delete this preset?')) return;
        try {
            await deleteIndicatorPreset(editSelectedPresetId);
            setEditIndicatorPresets(prev => prev.filter(p => String(p.id) !== String(editSelectedPresetId)));
            setEditSelectedPresetId('');
        } catch (e) {
            alert(e?.message || 'Failed to delete preset');
        }
    }, [editSelectedPresetId]);

    const rootClassName = overlay
        ? 'p-0'
        : (compact ? 'p-2 border-b border-gray-700' : 'p-4 border-b border-gray-700');
    const regularHeaderClassName = overlay
        ? 'pointer-events-none flex flex-wrap items-center justify-between gap-2'
        : 'mb-3 flex flex-wrap items-center justify-between gap-3';
    const compactHeaderClassName = overlay
        ? 'pointer-events-none flex flex-wrap items-center justify-between gap-2'
        : 'mb-2 flex flex-nowrap items-center justify-between gap-2 overflow-x-auto';
    const clusterPointerClassName = overlay ? 'pointer-events-auto' : '';
    const titleChip = overlay && toolbarTitle ? (
        React.isValidElement(toolbarTitle) ? (
            <div className="shrink-0">
                {toolbarTitle}
            </div>
        ) : (
            <span className="max-w-[14rem] truncate rounded-md px-2 py-1 text-[11px] font-semibold text-textPrimary/90" title={String(toolbarTitle)}>
                {toolbarTitle}
            </span>
        )
    ) : null;

    return (
        <div className={rootClassName}>
            {/* Header */}
            {!compact && (
                <div className={regularHeaderClassName}>
                    {/* Left: Indicators + Draw buttons */}
                    <div className={`chart-toolbar-cluster flex items-center gap-1.5 ${clusterPointerClassName}`}>
                    {titleChip}
                    <IndicatorsMenu
                        compact={false}
                        activeIndicators={activeIndicators}
                        getPrimarySeriesKey={getPrimarySeriesKey}
                        getEffectiveField={getEffectiveField}
                        toggleIndicatorVisibility={stableToggleIndicatorVisibility}
                        openVisualEditor={stableOpenVisualEditor}
                        onRemoveIndicator={stableOnRemoveIndicator}
                        indicatorOptions={indicatorOptions}
                        selectedIndicator={selectedIndicator}
                        setSelectedIndicator={setSelectedIndicator}
                        getMetaById={getMetaById}
                        getMetaByName={getMetaByName}
                        params={params}
                        handleParamChange={handleParamChange}
                        handleAddClick={stableHandleAddClick}
                        selectedSource={selectedSource}
                        setSelectedSource={setSelectedSource}
                        selectedSourceOutput={selectedSourceOutput}
                        setSelectedSourceOutput={setSelectedSourceOutput}
                        indicatorPresets={indicatorPresets}
                        selectedPresetId={selectedPresetId}
                        setSelectedPresetId={setSelectedPresetId}
                        onApplySelectedPreset={onApplySelectedPreset}
                        onSaveCurrentAsPreset={onSaveCurrentAsPreset}
                        onUpdateSelectedPresetParams={onUpdateSelectedPresetParams}
                        onRenameSelectedPreset={onRenameSelectedPreset}
                        onDeleteSelectedPreset={onDeleteSelectedPreset}
                    />
                    <DrawingToolbar
                        activeTool={activeDrawingTool}
                        onSelectTool={onSelectDrawingTool}
                        onDeleteSelected={onDeleteSelectedDrawing}
                        onDeleteAll={onDeleteAllDrawings}
                        onUpdateDrawing={onUpdateDrawing}
                        onSelectDrawing={onSelectDrawing}
                        hasSelection={drawingSelection}
                        selectedDrawingId={selectedDrawingId}
                        drawingCount={drawingCount || 0}
                        drawings={drawings || []}
                        compact={false}
                    />
                    </div>
                    <div className={`chart-toolbar-cluster flex items-center gap-1.5 overflow-x-auto min-w-0 flex-shrink ${clusterPointerClassName}`}>
                        {/* Timeframe selector shown inline with buttons if provided and not backtest */}
                        {!isBacktest && onTimeframeChange && (
                            <div className="w-40 shrink-0">
                                <CustomSelect
                                    options={timeframeOptions || [
                                        { label: '1M', value: 'M1' },
                                        { label: '5M', value: 'M5' },
                                        { label: '15M', value: 'M15' },
                                        { label: '30M', value: 'M30' },
                                        { label: '1H', value: 'H1' },
                                        { label: '4H', value: 'H4' },
                                        { label: '1D', value: 'D1' },
                                        { label: '1W', value: 'W1' },
                                        { label: '1MN', value: 'MN1' },
                                    ]}
                                    value={selectedTimeframe}
                                    onChange={onTimeframeChange}
                                    placeholder="Timeframe"
                                    buttonClassName="chart-toolbar-select-trigger"
                                    dropdownFitContent={true}
                                    optionPaddingLeft={16}
                                />
                            </div>
                        )}
                        {!isBacktest && onToggleRecentTrades && (
                            <Button
                                size="md"
                                variant="ghost"
                                onClick={onToggleRecentTrades}
                                title="Toggle open and historical trades overlay"
                                aria-pressed={showRecentTrades}
                                className={getChartToolbarToggleClassName('primary', showRecentTrades, false)}
                                style={CHART_TOOLBAR_BUTTON_STYLE}
                            >
                                <span className="flex items-center gap-2">
                                    <span className={`chart-toolbar-state-dot ${showRecentTrades ? 'chart-toolbar-state-dot-primary' : 'chart-toolbar-state-dot-muted'}`} />
                                    <span>Trades</span>
                                </span>
                            </Button>
                        )}
                        {!isBacktest && onToggleVolume && (
                            <Button
                                size="md"
                                variant="ghost"
                                onClick={onToggleVolume}
                                title="Toggle volume overlay"
                                aria-pressed={showVolume}
                                className={getChartToolbarToggleClassName('success', showVolume, false)}
                                style={CHART_TOOLBAR_BUTTON_STYLE}
                            >
                                <span className="flex items-center gap-2">
                                    <span className={`chart-toolbar-state-dot ${showVolume ? 'chart-toolbar-state-dot-success' : 'chart-toolbar-state-dot-muted'}`} />
                                    <span>Volume</span>
                                </span>
                            </Button>
                        )}
                        {!isBacktest && (
                            <ProfilesMenu
                                compact={false}
                                chartProfiles={chartProfiles}
                                selectedProfile={selectedProfile}
                                onLoadProfile={onLoadProfile}
                                onDeleteProfile={onDeleteProfile}
                                setIsSaveProfileOpen={setIsSaveProfileOpen}
                            />
                        )}
                        {onToggleFullscreen && (
                            <Button
                                type="button"
                                size="md"
                                variant="ghost"
                                onClick={onToggleFullscreen}
                                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen chart'}
                                aria-label={isFullscreen ? 'Exit fullscreen chart' : 'Fullscreen chart'}
                                aria-pressed={isFullscreen}
                                className={getChartToolbarIconClassName(isFullscreen, false)}
                                style={CHART_TOOLBAR_BUTTON_STYLE}
                            >
                                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {compact && (
                <div className={compactHeaderClassName}>
                    {/* Left: Indicators + Draw buttons */}
                    <div className={`chart-toolbar-cluster flex min-w-0 items-center gap-1 ${clusterPointerClassName}`}>
                    {titleChip}
                    <IndicatorsMenu
                        compact={true}
                        activeIndicators={activeIndicators}
                        getPrimarySeriesKey={getPrimarySeriesKey}
                        getEffectiveField={getEffectiveField}
                        toggleIndicatorVisibility={stableToggleIndicatorVisibility}
                        openVisualEditor={stableOpenVisualEditor}
                        onRemoveIndicator={stableOnRemoveIndicator}
                        indicatorOptions={indicatorOptions}
                        selectedIndicator={selectedIndicator}
                        setSelectedIndicator={setSelectedIndicator}
                        getMetaById={getMetaById}
                        getMetaByName={getMetaByName}
                        params={params}
                        handleParamChange={handleParamChange}
                        handleAddClick={stableHandleAddClick}
                        selectedSource={selectedSource}
                        setSelectedSource={setSelectedSource}
                        selectedSourceOutput={selectedSourceOutput}
                        setSelectedSourceOutput={setSelectedSourceOutput}
                        indicatorPresets={indicatorPresets}
                        selectedPresetId={selectedPresetId}
                        setSelectedPresetId={setSelectedPresetId}
                        onApplySelectedPreset={onApplySelectedPreset}
                        onSaveCurrentAsPreset={onSaveCurrentAsPreset}
                        onUpdateSelectedPresetParams={onUpdateSelectedPresetParams}
                        onRenameSelectedPreset={onRenameSelectedPreset}
                        onDeleteSelectedPreset={onDeleteSelectedPreset}
                    />
                    <DrawingToolbar
                        activeTool={activeDrawingTool}
                        onSelectTool={onSelectDrawingTool}
                        onDeleteSelected={onDeleteSelectedDrawing}
                        onDeleteAll={onDeleteAllDrawings}
                        onUpdateDrawing={onUpdateDrawing}
                        onSelectDrawing={onSelectDrawing}
                        hasSelection={drawingSelection}
                        selectedDrawingId={selectedDrawingId}
                        drawingCount={drawingCount || 0}
                        drawings={drawings || []}
                        compact={true}
                    />
                    </div>
                    <div className={`chart-toolbar-cluster flex flex-nowrap items-center gap-1 overflow-x-auto min-w-0 flex-shrink ${clusterPointerClassName}`}>
                        {/* Timeframe selector for compact mode */}
                        {!isBacktest && onTimeframeChange && (
                            <div className="w-24 sm:w-28">
                                <CustomSelect
                                    options={timeframeOptions || [
                                        { label: '1M', value: 'M1' },
                                        { label: '5M', value: 'M5' },
                                        { label: '15M', value: 'M15' },
                                        { label: '30M', value: 'M30' },
                                        { label: '1H', value: 'H1' },
                                        { label: '4H', value: 'H4' },
                                        { label: '1D', value: 'D1' },
                                        { label: '1W', value: 'W1' },
                                        { label: '1MN', value: 'MN1' },
                                    ]}
                                    value={selectedTimeframe}
                                    onChange={onTimeframeChange}
                                    placeholder="TF"
                                    buttonClassName="chart-toolbar-select-trigger"
                                    dropdownFitContent={true}
                                    optionPaddingLeft={16}
                                />
                            </div>
                        )}
                        {!isBacktest && onToggleRecentTrades && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={onToggleRecentTrades}
                                title="Toggle open and historical trades overlay"
                                aria-pressed={showRecentTrades}
                                className={getChartToolbarToggleClassName('primary', showRecentTrades, true)}
                                style={CHART_TOOLBAR_BUTTON_STYLE}
                            >
                                <span className="flex items-center gap-1.5 sm:gap-2">
                                    <span className={`chart-toolbar-state-dot ${showRecentTrades ? 'chart-toolbar-state-dot-primary' : 'chart-toolbar-state-dot-muted'}`} />
                                    <span>Trades</span>
                                </span>
                            </Button>
                        )}
                        {!isBacktest && onToggleVolume && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={onToggleVolume}
                                title="Toggle volume overlay"
                                aria-pressed={showVolume}
                                className={getChartToolbarToggleClassName('success', showVolume, true)}
                                style={CHART_TOOLBAR_BUTTON_STYLE}
                            >
                                <span className="flex items-center gap-1.5 sm:gap-2">
                                    <span className={`chart-toolbar-state-dot ${showVolume ? 'chart-toolbar-state-dot-success' : 'chart-toolbar-state-dot-muted'}`} />
                                    <span>Volume</span>
                                </span>
                            </Button>
                        )}
                        {!isBacktest && (
                            <ProfilesMenu
                                compact={true}
                                chartProfiles={chartProfiles}
                                selectedProfile={selectedProfile}
                                onLoadProfile={onLoadProfile}
                                onDeleteProfile={onDeleteProfile}
                                setIsSaveProfileOpen={setIsSaveProfileOpen}
                            />
                        )}
                        {onToggleFullscreen && (
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={onToggleFullscreen}
                                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen chart'}
                                aria-label={isFullscreen ? 'Exit fullscreen chart' : 'Fullscreen chart'}
                                aria-pressed={isFullscreen}
                                className={getChartToolbarIconClassName(isFullscreen, true)}
                                style={CHART_TOOLBAR_BUTTON_STYLE}
                            >
                                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Removed inline Active Indicators list and Add Indicator modal; both live inside the Indicators menu */}

            {/* Save Profile modal (kept) */}
            <Modal
                open={isSaveProfileOpen}
                onClose={() => setIsSaveProfileOpen(false)}
                title="Save Profile"
                footer={
                    <>
                        <Button variant="outline" size="sm" onClick={() => setIsSaveProfileOpen(false)}>Cancel</Button>
                        <Button size="sm" onClick={handleSaveProfileClick}>Save</Button>
                    </>
                }
            >
                <div className="space-y-2">
                    <label className="text-sm">Profile name</label>
                    <Input
                        placeholder="Enter profile name"
                        value={newProfileName}
                        onChange={(e) => setNewProfileName(e.target.value)}
                    />
                </div>
            </Modal>

            {/* Visual editor modal (kept) */}
            <Modal
                open={Boolean(editingIndicatorId)}
                onClose={closeVisualEditor}
                title={`${editingIndicator ? editingIndicator.name : 'Indicator'} settings`}
                footer={
                    editorTab === 'visuals' ? (
                        <>
                            <Button variant="outline" size="sm" onClick={() => setVisualDraft({})}>Reset</Button>
                            <Button size="sm" onClick={() => saveVisuals(true)}>Save</Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" size="sm" onClick={() => setParamDraft(editingIndicator?.params || {})}>Reset</Button>
                            <Button size="sm" onClick={saveParams}>Save</Button>
                        </>
                    )
                }
            >
                {/* Tabs */}
                <div className="mb-3 border-b border-borderDefault flex items-center gap-2">
                    <button className={`px-3 py-2 text-sm ${editorTab === 'visuals' ? 'border-b-2 border-primary text-textPrimary' : 'text-textSecondary'}`} onClick={() => setEditorTab('visuals')}>Visuals</button>
                    <button className={`px-3 py-2 text-sm ${editorTab === 'params' ? 'border-b-2 border-primary text-textPrimary' : 'text-textSecondary'}`} onClick={() => setEditorTab('params')}>Parameters</button>
                    <div className="flex-1" />
                    <div className="h-4">
                        {justSaved && editorTab === 'visuals' && <span className="text-green-400 text-xs">Saved</span>}
                    </div>
                </div>

                {editorTab === 'visuals' ? (
                    editingIndicator && editingSchema?.series ? (
                        Object.keys(editingSchema.series || {}).map((seriesKey) => {
                            const fields = editingSchema.series[seriesKey] || {};
                            const eff = (visualDraft.series && visualDraft.series[seriesKey]) || (editingDefaults.series && editingDefaults.series[seriesKey]) || {};
                            return (
                                <div key={seriesKey} className="mb-3">
                                    <div className="text-xs font-medium text-gray-300 mb-1">{seriesKey}</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        {Object.keys(fields).map((field) => {
                                            const fs = fields[field];
                                            const val = eff[field];
                                            if (fs?.type === 'string' && field === 'color') {
                                                return (
                                                    <label key={field} className="text-xs flex items-center gap-2">
                                                        <span className="w-24">Color</span>
                                                        <input type="color" value={val || '#ffd700'} onChange={(e) => handleVisualFieldChange(seriesKey, field, fs, e.target.value)} />
                                                    </label>
                                                );
                                            }
                                            if (fs?.type === 'string' && Array.isArray(fs.enum)) {
                                                return (
                                                    <label key={field} className="text-xs flex items-center gap-2">
                                                        <span className="w-24">{field}</span>
                                                        <select className="bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-xs" value={val || ''} onChange={(e) => handleVisualFieldChange(seriesKey, field, fs, e.target.value)}>
                                                            <option value="">--</option>
                                                            {fs.enum.map((opt) => (
                                                                <option key={opt} value={opt}>{opt}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                );
                                            }
                                            if (fs?.type === 'integer') {
                                                return (
                                                    <label key={field} className="text-xs flex items-center gap-2">
                                                        <span className="w-24">{field}</span>
                                                        <input
                                                            type="number"
                                                            className="bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-xs"
                                                            value={val ?? ''}
                                                            min={fs.min}
                                                            max={fs.max}
                                                            onChange={(e) => handleVisualFieldChange(seriesKey, field, fs, e.target.value)}
                                                        />
                                                    </label>
                                                );
                                            }
                                            if (fs?.type === 'boolean') {
                                                return (
                                                    <label key={field} className="text-xs flex items-center gap-2">
                                                        <span className="w-24">{field}</span>
                                                        <input type="checkbox" checked={Boolean(val)} onChange={(e) => handleVisualFieldChange(seriesKey, field, fs, e.target.checked)} />
                                                    </label>
                                                );
                                            }
                                            // fallback text
                                            return (
                                                <label key={field} className="text-xs flex items-center gap-2">
                                                    <span className="w-24">{field}</span>
                                                    <input
                                                        type="text"
                                                        className="bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-xs"
                                                        value={val ?? ''}
                                                        onChange={(e) => handleVisualFieldChange(seriesKey, field, fs, e.target.value)}
                                                    />
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-sm text-gray-400">No visual schema available.</div>
                    )
                ) : (
                    <div>
                        {editingIndicator && editingParamSchema ? (
                            <>
                                {/* Presets row for Edit (Parameters tab) */}
                                <div className="mb-3">
                                    <div className="mb-1 text-xs text-textSecondary">Presets</div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <div className="min-w-[220px] w-1/2 max-w-sm">
                                            <CustomSelect
                                                options={(editIndicatorPresets || []).map(p => ({ value: String(p.id), label: p.name }))}
                                                value={editSelectedPresetId}
                                                onChange={setEditSelectedPresetId}
                                                placeholder={(editIndicatorPresets && editIndicatorPresets.length) ? 'Choose preset' : 'No presets'}
                                                renderInPortal={false}
                                            />
                                        </div>
                                        <Button size="sm" variant="secondary" onClick={applyEditPreset} disabled={!editSelectedPresetId}>Apply</Button>
                                        <Button size="sm" variant="secondary" onClick={saveAsEditPreset} disabled={!editingIndicatorId}>Save as</Button>
                                        <Button size="sm" variant="outline" onClick={updateEditPresetParams} disabled={!editSelectedPresetId}>Update</Button>
                                        <Button size="sm" variant="outline" onClick={renameEditPreset} disabled={!editSelectedPresetId}>Rename</Button>
                                        <Button size="sm" variant="destructive" onClick={deleteEditPreset} disabled={!editSelectedPresetId}>Delete</Button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-1">
                                {Object.entries(editingParamSchema)
                                    .sort(([, a], [, b]) => {
                                        const ao = (a && (a.x_order ?? a['x-order'])) ?? Number.POSITIVE_INFINITY;
                                        const bo = (b && (b.x_order ?? b['x-order'])) ?? Number.POSITIVE_INFINITY;
                                        return ao - bo;
                                    })
                                    .map(([paramName, paramSchema]) => (
                                        <div key={paramName}>
                                            <label className="block text-xs font-medium mb-1">{paramSchema.display_name || paramName}</label>
                                            {(paramSchema.type === 'integer' || paramSchema.type === 'int' || paramSchema.type === 'float' || paramSchema.type === 'number') && (
                                                <Input
                                                    type="number"
                                                    className="w-1/2 min-w-[140px]"
                                                    value={paramDraft[paramName] ?? ''}
                                                    onChange={e => {
                                                        const raw = e.target.value;
                                                        if (raw === '') { setParamDraft(p => ({ ...p, [paramName]: '' })); return; }
                                                        const isInt = (paramSchema.type === 'integer' || paramSchema.type === 'int');
                                                        const v = isInt ? parseInt(raw, 10) : parseFloat(raw);
                                                        setParamDraft(p => ({ ...p, [paramName]: Number.isNaN(v) ? '' : v }));
                                                    }}
                                                    min={(paramSchema.min ?? paramSchema.minimum)}
                                                    max={(paramSchema.max ?? paramSchema.maximum)}
                                                    step={paramSchema.step || ((paramSchema.type === 'integer' || paramSchema.type === 'int') ? 1 : 'any')}
                                                />
                                            )}
                                            {paramSchema.type === 'string' && (Array.isArray(paramSchema.options) || Array.isArray(paramSchema.enum)) && (
                                                <CustomSelect
                                                    options={(paramSchema.options || paramSchema.enum).map(opt => ({ value: opt, label: String(opt) }))}
                                                    value={paramDraft[paramName] || ''}
                                                    onChange={value => setParamDraft(p => ({ ...p, [paramName]: value }))}
                                                    className="w-1/2 min-w-[140px]"
                                                    renderInPortal={false}
                                                />
                                            )}
                                            {paramSchema.type === 'string' && !Array.isArray(paramSchema.options) && !Array.isArray(paramSchema.enum) && (
                                                <Input
                                                    type="text"
                                                    className="w-1/2 min-w-[140px]"
                                                    value={paramDraft[paramName] ?? ''}
                                                    onChange={e => setParamDraft(p => ({ ...p, [paramName]: e.target.value }))}
                                                />
                                            )}
                                            {paramSchema.type === 'boolean' && (
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        id={`edit-param-${paramName}`}
                                                        type="checkbox"
                                                        checked={Boolean(paramDraft[paramName])}
                                                        onChange={e => setParamDraft(p => ({ ...p, [paramName]: e.target.checked }))}
                                                    />
                                                    <label htmlFor={`edit-param-${paramName}`} className="text-xs">{paramSchema.display_name || paramName}</label>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="text-sm text-gray-400">No parameters available.</div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default IndicatorControls;
